// Kiosk scanners pick up a web release at the next idle moment instead of at the next reboot.
//
// The page compares the commit it was built from with /version.json (written at build time by
// scripts/write-version.js) every few minutes and whenever the scanner goes idle, and reloads only
// while nobody is signed in. Someone mid-assembly is never interrupted; a scanner left on the
// badge screen updates within minutes. Browsers and phones keep the service worker's UpdatePrompt.

import { useEffect, useRef } from 'react';
import { getShell, inShell } from './shell';

const POLL_MS = 5 * 60 * 1000;
// Read at call time: the build inlines it either way, and tests can set it per case.
const runningSha = () => process.env.REACT_APP_COMMIT_SHA || '';

async function liveSha(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = await res.json();
    return typeof body?.sha === 'string' && body.sha ? body.sha : null;
  } catch {
    return null;
  }
}

/** `idle` is true while nobody is signed in on the scanner. */
export function useIdleUpdate(idle: boolean): void {
  const idleRef = useRef(idle);
  idleRef.current = idle;

  // The shell installs its own updates only while this is true.
  useEffect(() => {
    try { getShell()?.setIdle?.(idle); } catch { /* an old shell, or mid-teardown */ }
  }, [idle]);

  useEffect(() => {
    // No running commit (a local dev build) means nothing to compare, and comparing '' with a real
    // SHA would reload forever.
    const running = runningSha();
    if (!inShell() || !running) return undefined;
    let alive = true;
    const check = async () => {
      const live = await liveSha();
      if (alive && idleRef.current && live && live !== running) window.location.reload();
    };
    const timer = setInterval(check, POLL_MS);
    if (idle) check();
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [idle]);
}
